import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Grid, Paper, Stack, IconButton, Tooltip, 
    CircularProgress, Fade, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    TrendingUp as AttritionIcon,
    People as HeadcountIcon,
    PersonAdd as HiresIcon,
    PersonRemove as ExitsIcon
} from '@mui/icons-material';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, 
    Tooltip as ChartTooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';
import { fetchTurnoverReport } from '../../services/webReportsService';

// Premium color palette for charts and UI elements
const COLORS = ['primary.main', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

export default function CessationTurnoverReport({ setIsLoggedIn }) {
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const token = useMemo(() => sessionStorage.getItem("token") || "", []);

    // Filter States
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(String(currentYear));
    const [branchId, setBranchId] = useState("");
    const [orgNodeId, setOrgNodeId] = useState("");

    // Metadata States
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);

    // Data loading states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reportData, setReportData] = useState(null);

    // List of last 6 years for selection
    const yearsList = useMemo(() => {
        return Array.from({ length: 6 }, (_, i) => String(currentYear - i));
    }, [currentYear]);

    // Load branch and department dropdowns
    useEffect(() => {
        const fetchMetadata = async () => {
            const config = {
                withCredentials: true,
                headers: {
                    "X-Tenant": tenant,
                    "Authorization": `Bearer ${token}`
                }
            };
            try {
                const [branchRes, nodeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
                ]);

                if (Array.isArray(branchRes.data?.data)) setBranches(branchRes.data.data);
                if (Array.isArray(nodeRes.data?.data)) setDepartments(buildHierarchicalList(nodeRes.data.data));
            } catch (err) {
                console.error("Failed to load metadata filters:", err);
            }
        };

        if (tenant && token) {
            fetchMetadata();
        }
    }, [tenant, token]);

    // Fetch Report Data
    const loadReportData = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetchTurnoverReport({
                year,
                branch_id: branchId || undefined,
                org_node_id: orgNodeId || undefined
            });

            if (res.success && res.data) {
                setReportData(res.data);
            } else {
                throw new Error(res.message || "Failed to load report data");
            }
        } catch (err) {
            console.error("Error loading turnover report:", err);
            setError(err.message || "Failed to load turnover analytics data.");
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [year, branchId, orgNodeId]);

    useEffect(() => {
        if (tenant) {
            loadReportData();
        }
    }, [tenant, loadReportData]);

    // Calculate aggregated metrics from monthlyStats
    const summaryMetrics = useMemo(() => {
        if (!reportData || !reportData.monthlyStats || reportData.monthlyStats.length === 0) {
            return {
                closingHeadcount: 0,
                totalJoined: 0,
                totalExited: 0,
                avgTurnoverRate: 0
            };
        }

        const stats = reportData.monthlyStats;
        
        // Sum total joiners and exiters
        const totalJoined = stats.reduce((sum, item) => sum + (item.new_hires || 0), 0);
        const totalExited = stats.reduce((sum, item) => sum + (item.exits || 0), 0);

        // Find the latest active month with headcount details
        let closingHeadcount = 0;
        let activeMonthCount = 0;
        let turnoverSum = 0;

        for (let i = stats.length - 1; i >= 0; i--) {
            if (stats[i].closing_headcount > 0 || stats[i].new_hires > 0 || stats[i].exits > 0) {
                if (closingHeadcount === 0) {
                    closingHeadcount = stats[i].closing_headcount;
                }
            }
        }

        // Fallback to last month closing if everything is 0
        if (closingHeadcount === 0 && stats.length > 0) {
            closingHeadcount = stats[stats.length - 1].closing_headcount;
        }

        // Calculate simple average of turnover rates for months that had headcount/activity
        stats.forEach(item => {
            if (item.opening_headcount > 0 || item.closing_headcount > 0 || item.exits > 0) {
                activeMonthCount++;
                turnoverSum += item.turnover_rate || 0;
            }
        });

        const avgTurnoverRate = activeMonthCount > 0 
            ? parseFloat((turnoverSum / activeMonthCount).toFixed(2)) 
            : 0;

        return {
            closingHeadcount,
            totalJoined,
            totalExited,
            avgTurnoverRate
        };
    }, [reportData]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
                {/* Header Section */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <IconButton onClick={() => navigate('/reports')} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>Cessation & Turnover Analytics</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ color: '#64748b', ml: 5.5 }}>Analyze month-on-month workforce entry/exit dynamics and turnover trends.</Typography>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Tooltip title="Refresh Data">
                            <IconButton onClick={loadReportData} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Filters Panel */}
                <Paper sx={{ 
                    p: 3, borderRadius: 4, mb: 4, 
                    border: '1px solid #e2e8f0', 
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    bgcolor: '#fff'
                }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={year}
                                    label="Year"
                                    onChange={(e) => setYear(e.target.value)}
                                >
                                    {yearsList.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="branch-select-label">Branch</InputLabel>
                                <Select
                                    labelId="branch-select-label"
                                    value={branchId}
                                    label="Branch"
                                    onChange={(e) => setBranchId(e.target.value)}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {branches.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="dept-select-label">Department</InputLabel>
                                <Select
                                    labelId="dept-select-label"
                                    value={orgNodeId}
                                    label="Department"
                                    onChange={(e) => setOrgNodeId(e.target.value)}
                                >
                                    <MenuItem value="">All Departments</MenuItem>
                                    {departments.map(d => (
                                        <MenuItem key={d.id} value={d.id} sx={{ pl: 2 + (d._depth || 0) * 2 }}>
                                            {d._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                            {d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Loading State */}
                {loading ? (
                    <Box sx={{ height: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Calculating attrition rates & trends...</Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4, bgcolor: '#fee2e2', color: '#991b1b', borderRadius: 4, border: '1px solid #fecaca' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>Error Loading Analytics</Typography>
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                ) : (
                    <Fade in timeout={600}>
                        <Box>
                            {/* KPI Cards Row */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex', alignItems: 'center', gap: 2.5
                                    }}>
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#e0e7ff', color: 'primary.main', display: 'flex' }}>
                                            <HeadcountIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>CLOSING HEADCOUNT</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summaryMetrics.closingHeadcount}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex', alignItems: 'center', gap: 2.5
                                    }}>
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#d1fae5', color: '#10b981', display: 'flex' }}>
                                            <HiresIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>NEW HIRES (YTD)</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summaryMetrics.totalJoined}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex', alignItems: 'center', gap: 2.5
                                    }}>
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#fee2e2', color: '#ef4444', display: 'flex' }}>
                                            <ExitsIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>TOTAL EXITS (YTD)</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summaryMetrics.totalExited}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex', alignItems: 'center', gap: 2.5
                                    }}>
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#ffedd5', color: '#f59e0b', display: 'flex' }}>
                                            <AttritionIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>AVG. MONTHLY ATTRITION</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summaryMetrics.avgTurnoverRate}%</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Charts Row 1 */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                {/* Left Chart - Trend */}
                                <Grid item xs={12} lg={8}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                                        height: 480, display: 'flex', flexDirection: 'column'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>Headcount & Attrition Rate Trend</Typography>
                                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <ComposedChart data={reportData.monthlyStats} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                                                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} label={{ value: 'Employees', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#64748b', fontSize: 11, fontWeight: 600 } }} />
                                                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} label={{ value: 'Attrition Rate (%)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: '#64748b', fontSize: 11, fontWeight: 600 } }} />
                                                    <ChartTooltip 
                                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                                    />
                                                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                                                    
                                                    {/* Joiners and Leavers as side bars */}
                                                    <Bar yAxisId="left" dataKey="new_hires" name="New Hires" fill="#10b981" radius={[3, 3, 0, 0]} barSize={16} />
                                                    <Bar yAxisId="left" dataKey="exits" name="Exits" fill="#ef4444" radius={[3, 3, 0, 0]} barSize={16} />
                                                    
                                                    {/* Closing headcount as line */}
                                                    <Line yAxisId="left" type="monotone" dataKey="closing_headcount" name="Headcount" stroke="primary.main" strokeWidth={2.5} activeDot={{ r: 6 }} dot={{ r: 4 }} />
                                                    
                                                    {/* Attrition rate as line on right Y axis */}
                                                    <Line yAxisId="right" type="monotone" dataKey="turnover_rate" name="Turnover Rate (%)" stroke="#f59e0b" strokeWidth={2.5} activeDot={{ r: 6 }} dot={{ r: 4 }} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Right Chart - Department Exit Distribution */}
                                <Grid item xs={12} lg={4}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                                        height: 480, display: 'flex', flexDirection: 'column'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>Exits by Department</Typography>
                                        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {reportData.exitsByDepartment && reportData.exitsByDepartment.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={reportData.exitsByDepartment} layout="vertical" margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                                                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} width={90} />
                                                        <ChartTooltip 
                                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                                        />
                                                        <Bar dataKey="value" name="Exits" fill="#ef4444" radius={[0, 3, 3, 0]} barSize={16}>
                                                            {reportData.exitsByDepartment.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Bar>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No exits recorded in {year}.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Charts Row 2 & Table */}
                            <Grid container spacing={3}>
                                {/* Left Chart - Exit Status Breakdown */}
                                <Grid item xs={12} lg={4}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                                        height: 460, display: 'flex', flexDirection: 'column'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>Exit Status Breakdown</Typography>
                                        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {reportData.exitsByStatus && reportData.exitsByStatus.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={reportData.exitsByStatus}
                                                            cx="50%"
                                                            cy="45%"
                                                            innerRadius={60}
                                                            outerRadius={100}
                                                            paddingAngle={3}
                                                            dataKey="value"
                                                        >
                                                            {reportData.exitsByStatus.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <ChartTooltip 
                                                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: '12px' }}
                                                        />
                                                        <Legend 
                                                            verticalAlign="bottom" 
                                                            align="center" 
                                                            iconType="circle"
                                                            wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>No exits recorded in {year}.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* Right Table - Monthly Metrics Ledger */}
                                <Grid item xs={12} lg={8}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                                        height: 460, display: 'flex', flexDirection: 'column'
                                    }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>Monthly Metrics Ledger</Typography>
                                        <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Month</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Opening Headcount</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>New Hires</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Exits</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Closing Headcount</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Turnover Rate (%)</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {reportData.monthlyStats.map((row) => (
                                                        <TableRow key={row.month_num} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                            <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{row.month}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#334155' }}>{row.opening_headcount}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#10b981', fontWeight: 600 }}>+{row.new_hires}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#ef4444', fontWeight: 600 }}>-{row.exits}</TableCell>
                                                            <TableCell align="right" sx={{ color: '#334155' }}>{row.closing_headcount}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700, color: row.turnover_rate > 0 ? '#f59e0b' : '#64748b' }}>
                                                                {row.turnover_rate}%
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    </Fade>
                )}
            </Box>
        </Box>
    );
}
